import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { NgxMaskDirective } from 'ngx-mask';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';
import { PreselectionType } from '../../../../shared/enums/preselection-type';
import { Paragraph } from '../../../../shared/models/internal/popover/paragraph';
import { SearchResult } from '../../../../shared/models/internal/search-result';
import { CommonUtils, errorToString } from '../../../common/common-utils';
import { DtcApiService } from '../../../services/api/dtc-api.service';
import { InfoApiService } from '../../../services/api/info-api.service';
import { PreselectionService } from '../../../services/preselection.service';
import { ContactInfoComponent } from '../../common/contact-info/contact-info.component';
import { HelpPopoverComponent } from '../../common/popover/help-popover/help-popover.component';
import { SearchResultListComponent } from '../../search/search-result-list/search-result-list.component';
import { AgentActionDirective } from '../../../directives/agent-action.directive';

@Component({
  selector: 'app-index',
  imports: [
    RouterModule,
    SearchResultListComponent,
    FormsModule,
    ContactInfoComponent,
    NgxMaskDirective,
    HelpPopoverComponent,
    AgentActionDirective,
  ],
  templateUrl: './index.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './index.component.scss',
})
export class IndexComponent implements OnInit {
  public searchResults: SearchResult[] = [];
  public searchTerm = '';
  public versionInfo = environment.versionInfo;
  public backendVersionInfo = 'unknown';
  public backendEnvironment = 'unknown';
  public openApiUri = '';
  public invalidSearchTerm = false;
  public paragraphs: Paragraph[] = [];

  private dtcApi = inject(DtcApiService);
  private msalService = inject(MsalService);
  private infoApi = inject(InfoApiService);
  private preselectionService = inject(PreselectionService);
  private sanitizer = inject(DomSanitizer);

  public ngOnInit(): void {
    this.fetchApiInfo();
    this.buildHelpParagraphs();
    this.buildOpenApiSpecUri();
  }

  public get isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  public search(): void {
    if (this.searchTerm.length != 11) {
      this.invalidSearchTerm = true;
      this.searchResults = [];
      return;
    }
    this.dtcApi
      .getSignificantVersions(this.searchTerm)
      .pipe(
        map((items) => {
          return items.map((dtcRange) => {
            const swPartNumber =
              dtcRange.from?.id == dtcRange.to?.id
                ? CommonUtils.maskSwPartNumberMinorRelease(dtcRange.to?.swPartNumber?.number)
                : CommonUtils.maskSwPartNumberMinorRelease(dtcRange.to?.swPartNumber?.number) +
                  ' > ' +
                  CommonUtils.maskSwPartNumberMinorRelease(
                    dtcRange.from?.swPartNumber?.majorRelease + '.' + dtcRange.from?.swPartNumber?.minorRelease
                  );
            return {
              title: dtcRange.to?.displayId,
              description: '',
              id: dtcRange.from?.id,
              swPartNumber: swPartNumber,
              itemType: PreselectionType.Dtc,
            } as SearchResult;
          });
        })
      )
      .subscribe({
        next: (results) => {
          if (results.length == 1) {
            const dtcId = results[0].id;
            if (dtcId) {
              this.preselectionService.configureAndChangeRoute(dtcId, results[0].title ?? '', PreselectionType.Dtc);
            }
          } else {
            this.searchResults = results;
          }
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
  }

  public onKeyUp(event: KeyboardEvent): void {
    this.invalidSearchTerm = false;

    if (event.key === 'Enter') {
      this.search();
    } else if (event.key === 'Escape') {
      this.searchTerm = '';
    }
  }

  private fetchApiInfo(): void {
    if (!this.isLoggedIn) return;

    this.infoApi.getInfo().subscribe({
      next: (info) => {
        this.backendVersionInfo = info.versionInfo ?? 'unknown';
        this.backendEnvironment = info.environment ?? 'unknown';
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private buildOpenApiSpecUri(): void {
    this.openApiUri = environment.coreApiDocUrl;
  }

  private buildHelpParagraphs(): void {
    const kbd = (key: string): string =>
      `<kbd class="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">${key}</kbd>`;

    this.paragraphs = [
      {
        header: 'How to use? 🤔',
        content: this.sanitizer.bypassSecurityTrustHtml(
          '___.__.___.___ is the format for searching DTCs. In the first column, you need to specify the NW-Number. In the next columns, you can specify the DTC separated by a dot. For example, if you want to search for DTC 16512072 of NW 405, you would enter it as 405.16.512.072'
        ),
      },
      {
        header: 'Search',
        content: this.sanitizer.bypassSecurityTrustHtml(
          'After entering the search term, click the "Search for DTC" button or press ' +
            kbd('Enter') +
            ' to initiate the search.'
        ),
      },
      {
        header: '',
        content: this.sanitizer.bypassSecurityTrustHtml(
          'To cancel the search and clear the input field and the results, press ' + kbd('Esc')
        ),
      },
    ];
  }
}
